import React from 'react'
import TradeHistoryGrid from './TradeHistoryGrid'
import { mmyyyy } from '@prescreen/lib/date-utils'
import { dollars } from '@prescreen/lib/formats'
import { EquifaxTrade } from '@prescreen/lib/equifax-prequal-report'

interface TradeAccountProps {
  trade: EquifaxTrade
}

const TradeAccount = ({ trade }: TradeAccountProps) => {
  const leftColumn = [
    { label: 'Account Type:', value: trade.accountTypeCode?.description || '' },
    { label: 'Account Owner:', value: trade.accountDesignator?.description || '' },
    { label: 'Rate/Status:', value: trade.rate?.description || '' },
    { label: 'Date Opened:', value: mmyyyy(trade.dateOpened) || '' },
    { label: 'Date Reported:', value: mmyyyy(trade.dateReported) || '' },
    { label: 'Last Payment Date:', value: mmyyyy(trade.lastPaymentDate) || '' },
  ]

  const rightColumn = [
    { label: 'Balance:', value: dollars(trade.balance || 0, 0) },
    { label: 'Credit Limit:', value: dollars(trade.creditLimit || 0, 0) },
    { label: 'High Credit:', value: dollars(trade.highCredit || 0, 0) },
    { label: 'Scheduled Payment:', value: dollars(trade.scheduledPaymentAmount || 0, 0) },
    { label: 'Actual Payment:', value: dollars((trade as any).actualPaymentAmount || 0, 0) },
    { label: 'Last Payment Date:', value: mmyyyy(trade.lastPaymentDate) || '' }
  ]

  const getPortfolioInfo = (rawValue: string | undefined) => {
    const type = rawValue?.split(" ")[0]?.toLowerCase() || ''

    switch (type) {
      case 'mortgage':
        return { className: 'border border-blue-500 text-blue-500', text: 'Mortgage' }
      case 'revolving':
        return { className: 'border border-yellow-500 text-yellow-500', text: 'Revolving' }
      case 'line-of-credit':
      case 'line':
        return { className: 'border border-cyan-500 text-cyan-500', text: 'Line of Credit' }
      case 'installment':
        return { className: 'border border-green-500 text-green-500', text: 'Installment' }
      default:
        return { className: 'border border-gray-500 text-gray-500', text: 'Other' }
    }
  }

  const portfolioInfo = getPortfolioInfo(trade.portfolioTypeCode?.description)

  const delinquency = trade.thirtyDayCounter ||
    trade.ninetyDayCounter ||
    trade.sixtyDayCounter

  const latePaymentsTable = (
    <div className="mt-2">
      <table className="border border-gray-300 text-center text-xs">
        <thead>
          <tr className="bg-gray-100">
            <th className="px-2 py-1 font-normal">30</th>
            <th className="px-2 py-1 font-normal">60</th>
            <th className="px-2 py-1 font-normal">90</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="px-2 py-1">{trade.thirtyDayCounter || 0}</td>
            <td className="px-2 py-1">{trade.sixtyDayCounter || 0}</td>
            <td className="px-2 py-1">{trade.ninetyDayCounter || 0}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="account-report border rounded-lg p-3 mb-4 bg-white">
      <div className="flex justify-between items-center mb-2">
        <div className={`rounded px-2 py-0.5 text-xs font-medium ${portfolioInfo.className}`}>
          {portfolioInfo.text}
        </div>
        <div className="text-right">
          <div className="font-semibold text-xs text-gray-700">Account Number</div>
          <div className="text-xs font-mono text-gray-900">{trade.accountNumber}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {/* Left Column - Account Details */}
        <div className="space-y-0.5 min-w-0">
          {leftColumn.map((row) => (
            <div key={row.label} className="flex justify-between items-start text-xs leading-tight">
              <span className="text-gray-600 pr-1 flex-shrink-0">{row.label}</span>
              <span className="text-gray-900 text-right truncate">{row.value || '-'}</span>
            </div>
          ))}
        </div>

        {/* Middle Column - Financial Details */}
        <div className="space-y-0.5 min-w-0">
          {rightColumn.map((row) => (
            <div key={row.label} className="flex justify-between items-start text-xs leading-tight">
              <span className="text-gray-600 pr-1 flex-shrink-0">{row.label}</span>
              <span className="text-gray-900 text-right font-mono">{row.value || '-'}</span>
            </div>
          ))}
          {delinquency ? (
            <div className="mt-1">
              {latePaymentsTable}
            </div>
          ) : null}
        </div>

        {/* Right Column - Payment History */}
        <div className="space-y-1 min-w-0">
          {trade.paymentHistory1to24 ? (
            <TradeHistoryGrid trade={trade} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default TradeAccount

